import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Paper,
  Alert,
  Pagination,
  TextField,
  InputAdornment,
  Grid,
  FormControl,
  Select,
  MenuItem
} from '@mui/material';
import axios from 'axios';
import { api } from "../context/AuthContext";
import { Search, Visibility, Psychology, Feedback, FilterList } from '@mui/icons-material';

function SuperAdminInterviews() {
  const [interviews, setInterviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All');
  const [page, setPage] = useState(1);
  const [selectedInterview, setSelectedInterview] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const formatStatus = (status, jobType) => {
    if (!status) return "N/A";

    const isBPO = jobType === "non_technical_bpo";
    const s = status.toLowerCase();

    if (isBPO) {
      if (s.includes("ai_technical_interview_scheduled"))
        return "Communication & Customer Support Assessment Scheduled";

      if (s.includes("ai_technical_interview_completed"))
        return "Communication & Customer Support Assessment Completed";

      if (s.includes("technical_l2_interview_scheduled"))
        return "Customer Support L2 Interview Scheduled";

      if (s.includes("technical_l2_interview_completed"))
        return "Customer Support L2 Interview Completed";

      if (s.includes("technical_l2_interview_cancelled"))
        return "Customer Support L2 Interview Cancelled";

      if (s.includes("ai_hr_interview_scheduled"))
        return "Operations & HR Evaluation Scheduled";

      if (s.includes("ai_hr_interview_completed"))
        return "Operations & HR Evaluation Completed";

      if (s.includes("hr_l2_interview_scheduled"))
        return "Operations & HR Evaluation L2 Interview Scheduled";

      if (s.includes("hr_l2_interview_completed"))
        return "Operations & HR Evaluation L2 Interview Completed";

      if (s.includes("hr_l2_interview_cancelled"))
        return "Operations & HR Evaluation L2 Interview Cancelled";
    }

    if (s === "offered") return "Offer Released";

    return status
        .toLowerCase()
        .replace(/_/g, " ")
        .replace(/\b\w/g, (char) => char.toUpperCase())
        .replace(/\bai\b/gi, "AI")
        .replace(/\bhr\b/gi, "HR");
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      in_progress: 'info',
      shortlisted: 'success',
      rejected: 'error',
      offered: 'success',
    };
    return colors[status] || 'default';
  };

  useEffect(() => {
    const fetchInterviews = async () => {
      try {
        const response = await api.get('/super-admin/interviews');
        setInterviews(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        console.error('Error fetching interviews:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchInterviews();
  }, []);

  const filteredInterviews = interviews.filter(app => {
    const searchStr = searchTerm.toLowerCase();
    const matchesSearch = (
      (app.candidate?.firstName?.toLowerCase().includes(searchStr)) ||
      (app.candidate?.lastName?.toLowerCase().includes(searchStr)) ||
      (app.candidate?.email?.toLowerCase().includes(searchStr)) ||
      (app.job?.title?.toLowerCase().includes(searchStr))
    );

    const matchesFilter =
        statusFilter === 'All' ||
        formatStatus(app.status, app.job?.job_type) === statusFilter;

    return matchesSearch && matchesFilter;
  });

  const statusOptions = Array.from(new Set([
    'All',
    'Pending',
    'Shortlisted',
    'Selected',
    'Offer Released',
    'Rejected',
    'Terminated',
    'Completed',
    'Interview Scheduled',
    'Technical Interview Cancelled',
    'HR Interview Cancelled',
    'AI Coding Scheduled',
    ...interviews.map(app => formatStatus(app.status, app.job?.job_type))
  ])).filter(Boolean);

  const paginatedInterviews = filteredInterviews.slice((page - 1) * 10, page * 10);
  const totalPages = Math.ceil(filteredInterviews.length / 10);

  const handleOpenReport = (interview) => {
    setSelectedInterview(interview);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedInterview(null);
  };


  const renderAssessmentSummary = (title, assessment, icon) => {
    if (!assessment) return null;

    const feedback = assessment.feedback || assessment.notes;
    const score = assessment.score;
    const maxScore = assessment.max_score || assessment.maxScore || 100;

    return (
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {icon}
          <Typography variant="subtitle1" sx={{ ml: 1, fontWeight: 'bold' }}>
            {title}
          </Typography>
          {score !== undefined && score !== null && (
            <Chip 
              label={`Score: ${score}/${maxScore}`} 
              size="small" 
              color={score >= (maxScore * 0.7) ? 'success' : (score >= (maxScore * 0.4) ? 'warning' : 'error')}
              sx={{ ml: 2 }}
            />
          )}
        </Box>
        <Paper variant="outlined" sx={{ p: 2, bgcolor: '#fcfcfc', borderRadius: 0 }}>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            AI Feedback / Notes:
          </Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {feedback || 'No feedback available.'}
          </Typography>
        </Paper>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container sx={{ py: 4 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, flexWrap: 'wrap', gap: 2 }}>
        <Box>
          {/*<Typography
            sx={{
              color: "#1E3A8A",
              fontSize: {
                xs: "2rem",
                sm: "2.3rem",
                md: "2.6rem",
              },
              fontWeight: 800,
              lineHeight: 1.2,
              mb: 0.8,
            }}
          >*/}
          <Typography
              variant="h4"
              sx={{
                color: "#1E3A8A",
                fontWeight: "bold",
                fontSize: { xs: "1.5rem", md: "2rem" }
              }}
          >
            All Interviews
          </Typography>

          <Typography
            sx={{
              color: "text.secondary",
              fontSize: "1rem",
              fontWeight: 400,
            }}
          >
            Monitor interview progress, assessment results, and candidate performance across the platform.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search interviews..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search />
                </InputAdornment>
              ),
            }}
            sx={{ width: 300 }}
          />
          <FormControl size="small">
            <Select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
              displayEmpty
              renderValue={() => (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1E3A8A', fontWeight: 'bold' }}>
                  <FilterList sx={{ fontSize: 20 }} />
                  Filters
                </Box>
              )}
              sx={{
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E3A8A',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#1E40AF',
                },
                '& .MuiSelect-select': {
                  display: 'flex',
                  alignItems: 'center',
                }
              }}
            >
              <MenuItem disabled sx={{ fontWeight: 'bold', color: '#1E3A8A', borderBottom: '1px solid #eee', justifyContent: 'center', opacity: '1 !important' }}>
                Filters
              </MenuItem>
              {statusOptions.map((option) => (
                <MenuItem key={option} value={option}>{option}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: 0, boxShadow: 3, overflowX: 'auto' }}>
        <Table sx={{ minWidth: { xs: 800, md: '100%' } }}>
          <TableHead sx={{ bgcolor: '#f5f5f5' }}>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Candidate</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Job Title</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Applied Date</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Assessments</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedInterviews.map((app) => (
              <TableRow key={app.id} hover>
                <TableCell sx={{ fontWeight: 'medium' }}>
                  {app.candidate?.firstName} {app.candidate?.lastName}
                </TableCell>
                <TableCell>{app.candidate?.email}</TableCell>
                <TableCell>{app.job?.title || 'Unknown Job'}</TableCell>
                <TableCell>
                  {new Date(app.created_at || app.createdAt).toLocaleDateString()}
                </TableCell>
                <TableCell>
                  <Chip
                      label={formatStatus(app.status, app.job?.job_type)}
                    color={getStatusColor(app.status)}
                    size="small"
                    variant="outlined"
                  />
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5 }}>

                    {app.codingTest && app.job?.job_type !== "non_technical_bpo" && (
                        <Chip
                            label="Coding"
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                    )}

                    {app.technicalInterview && (
                        <Chip
                            label={
                              app.job?.job_type === "non_technical_bpo"
                                  ? "Communication"
                                  : "Tech"
                            }
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                    )}

                    {app.hrInterview && (
                        <Chip
                            label={
                              app.job?.job_type === "non_technical_bpo"
                                  ? "Operations"
                                  : "HR"
                            }
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                        />
                    )}

                  </Box>
                </TableCell>
                <TableCell>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={<Visibility />}
                    onClick={() => handleOpenReport(app)}
                  >
                    Report
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {filteredInterviews.length === 0 && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                  <Typography color="text.secondary">No interview records found.</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 3,
            flexWrap: "wrap",
            gap: 2,
          }}
      >
        <Typography variant="body2" color="text.secondary">
          {paginatedInterviews.length > 0 ? (page - 1) * 10 + 1 : 0}–
          {Math.min(page * 10, filteredInterviews.length)} of{" "}
          {filteredInterviews.length} records
        </Typography>
        <Pagination
            count={totalPages}
            page={page}
            onChange={(e, value) => setPage(value)}
            shape="rounded"
            color="primary"
            sx={{
              "& .MuiPaginationItem-root.Mui-selected": {
                backgroundColor: "#1E3A8A",
                "&:hover": {
                  backgroundColor: "#1E40AF",
                },
              },
            }}
        />
      </Box>

      {/* AI Report Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white' }}>
          AI Feedback Report - {selectedInterview?.candidate?.firstName} {selectedInterview?.candidate?.lastName}
        </DialogTitle>
        <DialogContent dividers>
          {selectedInterview && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Application Details
              </Typography>
              <Grid container spacing={2} sx={{ mb: 4 }}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Position:</Typography>
                  <Typography variant="body1" fontWeight="bold">{selectedInterview.job?.title}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Company:</Typography>
                  <Typography variant="body1">{selectedInterview.job?.company_name || selectedInterview.job?.companyName}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Application Date:</Typography>
                  <Typography variant="body1">{new Date(selectedInterview.created_at || selectedInterview.createdAt).toLocaleDateString()}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" color="text.secondary">Current Status:</Typography>
                  <Chip
                      label={formatStatus(
                          selectedInterview.status,
                          selectedInterview.job?.job_type
                      )}
                      color={getStatusColor(selectedInterview.status)}
                      size="small"
                  />
                </Grid>
              </Grid>

              <Divider sx={{ mb: 4 }} />

              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                <Feedback sx={{ mr: 1 }} /> AI Assessments Feedback
              </Typography>

              {selectedInterview.job?.job_type !== "non_technical_bpo" &&
                  renderAssessmentSummary(
                      'Coding Test',
                      selectedInterview.codingTest,
                      <Psychology />
                  )
              }
              {renderAssessmentSummary(
                  selectedInterview.job?.job_type === "non_technical_bpo"
                      ? "Communication & Customer Support Assessment"
                      : "Technical Interview",
                  selectedInterview.technicalInterview,
                  <Psychology />
              )}
              {renderAssessmentSummary(
                  selectedInterview.job?.job_type === "non_technical_bpo"
                      ? "Operations & HR Evaluation"
                      : "HR Interview",
                  selectedInterview.hrInterview,
                  <Psychology />
              )}
              {!(selectedInterview.codingTest || selectedInterview.technicalInterview || selectedInterview.hrInterview) && (
                <Alert severity="info">No assessments have been completed yet for this candidate.</Alert>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="primary">Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}

export default SuperAdminInterviews;
